/**
 * Literafan Web Push Service
 * Permite que leitores se inscrevam via Web Push API para receberem alertas no celular e desktop
 * mesmo com o navegador fechado quando novos capítulos forem publicados.
 */

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export interface WebPushStatus {
  isSupported: boolean;
  permission: NotificationPermission | 'unsupported';
  isSubscribed: boolean;
  subscription: PushSubscription | null;
}

export const isWebPushSupported = (): boolean => {
  if (typeof window === 'undefined') return false;
  return (
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
};

export const getServiceWorkerRegistration = async (): Promise<ServiceWorkerRegistration | null> => {
  if (!isWebPushSupported()) return null;

  try {
    // 1. Tenta obter registro ativo no escopo '/'
    let reg = await navigator.serviceWorker.getRegistration('/');
    if (!reg) {
      reg = await navigator.serviceWorker.register('/sw.js', { scope: '/' });
    }

    // 2. Aguarda o Service Worker estar pronto e ativo com timeout de resiliência
    const readyReg = await Promise.race([
      navigator.serviceWorker.ready,
      new Promise<ServiceWorkerRegistration | null>((resolve) => setTimeout(() => resolve(reg || null), 2500))
    ]);
    return readyReg || reg;
  } catch (err) {
    console.warn('[WebPush] Erro ao obter/registrar Service Worker em /sw.js:', err);
    try {
      // Fallback para registro direto do sw-push.js
      const fallbackReg = await navigator.serviceWorker.register('/sw-push.js', { scope: '/' });
      const readyFallback = await Promise.race([
        navigator.serviceWorker.ready,
        new Promise<ServiceWorkerRegistration | null>((resolve) => setTimeout(() => resolve(fallbackReg || null), 2500))
      ]);
      return readyFallback || fallbackReg;
    } catch (e2) {
      console.error('[WebPush] Falha total ao registrar Service Worker para Push:', e2);
      return null;
    }
  }
};

/**
 * Sincroniza silenciosamente a subscrição push em segundo plano se o usuário já concedeu permissão.
 * Essencial para PWAs instalados, navegadores em segundo plano e renovação de endpoints.
 */
export const autoSyncWebPushSubscription = async (email?: string): Promise<boolean> => {
  if (!isWebPushSupported() || typeof window === 'undefined') return false;
  if (Notification.permission !== 'granted') return false;

  try {
    const reg = await getServiceWorkerRegistration();
    if (!reg) return false;

    const res = await fetch('/api/push/vapid-public-key');
    if (!res.ok) return false;
    const { publicKey } = await res.json();
    if (!publicKey) return false;

    const applicationServerKey = urlBase64ToUint8Array(publicKey);
    let subscription = await reg.pushManager.getSubscription();

    // Se já houver subscrição, verifica se a chave bate
    if (subscription) {
      try {
        const currentKeyRaw = subscription.options.applicationServerKey;
        if (currentKeyRaw) {
          const cArr = Array.from(new Uint8Array(currentKeyRaw));
          const tArr = Array.from(applicationServerKey);
          if (cArr.length !== tArr.length || !cArr.every((v, i) => v === tArr[i])) {
            console.log('[WebPush] Chave VAPID alterada, renovando subscrição...');
            await subscription.unsubscribe();
            subscription = null;
          }
        }
      } catch {}
    }

    // Se não houver subscrição válida, cria uma nova
    if (!subscription) {
      subscription = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey,
      });
    }

    if (subscription) {
      const subJson = subscription.toJSON();
      const isStandalone = window.matchMedia('(display-mode: standalone)').matches ||
        (window.navigator as any).standalone === true;

      await fetch('/api/push/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          subscription: subJson,
          topics: ['articles', 'chapters', 'all'],
          email: email || undefined,
          userAgent: navigator.userAgent,
          isStandalone,
        }),
      });

      localStorage.setItem('literafan_push_subscribed', 'true');
      localStorage.setItem('literafan_notifications_enabled', 'true');

      window.dispatchEvent(new CustomEvent('literafan:push_status_change', {
        detail: { isSubscribed: true }
      }));

      return true;
    }
    return false;
  } catch (err) {
    console.debug('[WebPush] Auto-sync falhou silenciosamente:', err);
    return false;
  }
};


export const getWebPushStatus = async (): Promise<WebPushStatus> => {
  if (!isWebPushSupported()) {
    return {
      isSupported: false,
      permission: 'unsupported',
      isSubscribed: false,
      subscription: null,
    };
  }

  const permission = Notification.permission;
  const reg = await getServiceWorkerRegistration();

  if (!reg) {
    return {
      isSupported: true,
      permission,
      isSubscribed: false,
      subscription: null,
    };
  }

  try {
    const sub = await reg.pushManager.getSubscription();
    return {
      isSupported: true,
      permission,
      isSubscribed: Boolean(sub),
      subscription: sub,
    };
  } catch (err) {
    console.warn('[WebPush] Erro ao verificar subscrição:', err);
    return {
      isSupported: true,
      permission,
      isSubscribed: false,
      subscription: null,
    };
  }
};

export const subscribeToWebPush = async (
  topics: string[] = ['articles', 'chapters', 'all'],
  email?: string
): Promise<{
  success: boolean;
  message?: string;
  subscription?: PushSubscription;
  error?: string;
}> => {
  if (!isWebPushSupported()) {
    return {
      success: false,
      error: 'Seu navegador não possui suporte à API de Web Push.',
    };
  }

  try {
    // 1. Solicita permissão do usuário caso ainda não esteja concedida
    let permission = Notification.permission;
    if (permission === 'default') {
      permission = await Notification.requestPermission();
    }

    if (permission !== 'granted') {
      return {
        success: false,
        error: permission === 'denied' 
          ? 'As notificações foram bloqueadas no seu navegador. Para ativá-las, autorize o site nas configurações de permissões do navegador.' 
          : 'A permissão de notificações não foi confirmada.',
      };
    }

    // 2. Obtém o registro do Service Worker
    const reg = await getServiceWorkerRegistration();
    if (!reg) {
      return {
        success: false,
        error: 'Não foi possível inicializar o Service Worker para notificações push.',
      };
    }

    // 3. Obtém a chave pública VAPID do servidor
    const res = await fetch('/api/push/vapid-public-key');
    if (!res.ok) {
      throw new Error(`Falha ao obter chave VAPID: status ${res.status}`);
    }
    const { publicKey } = await res.json();
    if (!publicKey) {
      throw new Error('Chave pública VAPID vazia ou inválida retornada pelo servidor.');
    }

    const applicationServerKey = urlBase64ToUint8Array(publicKey);

    // 4. Se já houver subscrição anterior, reutiliza ou renova se a chave diferir
    let subscription = await reg.pushManager.getSubscription();
    if (subscription) {
      try {
        const currentKeyRaw = subscription.options.applicationServerKey;
        if (currentKeyRaw) {
          const cArr = Array.from(new Uint8Array(currentKeyRaw));
          const tArr = Array.from(applicationServerKey);
          if (cArr.length !== tArr.length || !cArr.every((v, i) => v === tArr[i])) {
            console.log('[WebPush] Chave VAPID alterada na subscrição manual, renovando...');
            await subscription.unsubscribe();
            subscription = null;
          }
        }
      } catch {}
    }

    if (!subscription) {
      subscription = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey,
      });
    }

    // 5. Envia os dados de subscrição para o backend persistir no Firestore
    const subJson = subscription.toJSON();
    const saveRes = await fetch('/api/push/subscribe', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        subscription: subJson,
        topics,
        email: email ? email.trim() : undefined,
        userAgent: navigator.userAgent,
      }),
    });

    if (!saveRes.ok) {
      throw new Error(`Falha ao salvar subscrição no servidor: status ${saveRes.status}`);
    }

    localStorage.setItem('literafan_push_subscribed', 'true');
    localStorage.setItem('literafan_notifications_enabled', 'true');

    // Emite evento para os componentes atualizarem o estado
    window.dispatchEvent(new CustomEvent('literafan:push_status_change', {
      detail: { isSubscribed: true }
    }));

    return {
      success: true,
      message: email 
        ? 'Notificações de artigos e inscrição na newsletter confirmadas!' 
        : 'Notificações de novos artigos e capítulos ativadas com sucesso!',
      subscription,
    };
  } catch (err: any) {
    console.error('[WebPush] Erro ao subscrever:', err);
    return {
      success: false,
      error: err.message || 'Ocorreu um erro ao ativar as notificações push.',
    };
  }
};

export const unsubscribeFromWebPush = async (): Promise<{
  success: boolean;
  message?: string;
  error?: string;
}> => {
  if (!isWebPushSupported()) {
    return { success: false, error: 'Web Push não suportado.' };
  }

  try {
    const reg = await getServiceWorkerRegistration();
    if (reg) {
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        const endpoint = sub.endpoint;
        await sub.unsubscribe();

        // Notifica o backend para remover
        await fetch('/api/push/unsubscribe', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ endpoint }),
        }).catch(() => {});
      }
    }

    localStorage.removeItem('literafan_push_subscribed');

    window.dispatchEvent(new CustomEvent('literafan:push_status_change', {
      detail: { isSubscribed: false }
    }));

    return {
      success: true,
      message: 'Notificações push desativadas.',
    };
  } catch (err: any) {
    console.error('[WebPush] Erro ao cancelar subscrição:', err);
    return {
      success: false,
      error: err.message || 'Erro ao cancelar notificações.',
    };
  }
};

export const sendTestWebPush = async (): Promise<{
  success: boolean;
  message?: string;
  error?: string;
}> => {
  try {
    const reg = await getServiceWorkerRegistration();
    const sub = reg ? await reg.pushManager.getSubscription() : null;

    const res = await fetch('/api/push/send-test', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        subscription: sub ? sub.toJSON() : null,
      }),
    });

    const data = await res.json();
    if (!res.ok) {
      throw new Error(data.error || 'Falha ao enviar notificação push de teste.');
    }

    return {
      success: true,
      message: data.message || 'Notificação push de teste enviada!',
    };
  } catch (err: any) {
    console.error('[WebPush] Erro no teste push:', err);
    return {
      success: false,
      error: err.message || 'Erro ao enviar notificação de teste.',
    };
  }
};

export interface ChapterNotificationPayload {
  title: string;
  chapterTitle?: string;
  slug?: string;
  excerpt?: string;
  imageUrl?: string;
  category?: string;
  author?: string;
  articleId?: string;
  sendNewsletter?: boolean;
  sendPush?: boolean;
}

export const dispatchChapterPushNotification = async (
  payload: ChapterNotificationPayload
): Promise<{
  success: boolean;
  sentCount?: number;
  totalSubscribers?: number;
  emailSentCount?: number;
  totalNewsletterSubscribers?: number;
  error?: string;
}> => {
  try {
    const res = await fetch('/api/articles/notify-publication', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    const data = await res.json();
    if (!res.ok) {
      throw new Error(data.error || 'Falha ao enviar notificação para os leitores.');
    }

    return {
      success: true,
      sentCount: data.pushSentCount !== undefined ? data.pushSentCount : data.sentCount,
      totalSubscribers: data.totalPushSubscribers !== undefined ? data.totalPushSubscribers : data.totalSubscribers,
      emailSentCount: data.emailSentCount,
      totalNewsletterSubscribers: data.totalNewsletterSubscribers,
    };
  } catch (err: any) {
    console.error('[Notification] Erro ao disparar notificação:', err);
    return {
      success: false,
      error: err.message || 'Erro ao disparar notificação de nova publicação.',
    };
  }
};

export const dispatchPublicationNotification = dispatchChapterPushNotification;
export const autoSyncWebPushIfGranted = autoSyncWebPushSubscription;

